import { Contact, ContactType } from "@repo/server-api";
import { ContactExtended, RuleFactoryType, TabKeys } from "@repo/server-api/types";
import { PersonIdentification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useState, useRef, useMemo } from "react";

import { setContactValues, validErrorState } from "@repo/legacy/components/helper/Index";
import {
	useCreatePersonContact,
	useUpdatePersonContact,
} from "@repo/legacy/components/hooks/ContactMutationHooks";
import ContactInfo from "@repo/legacy/components/shared/additionalInformation/ContactInfo";
import DialogActions from "@repo/legacy/components/shared/sections/DialogActions";

import DialogTabs from "./DialogWithTabs";
import Address from "../additionalInformation/Address";
import DepartmentInfo from "../additionalInformation/DepartmentInformation";

export default function DialogContactPerson(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: (value: ContactExtended) => void;
		contact: ContactExtended | null;
		setContact: (value: ContactExtended | null) => void;
		ruleFactory: RuleFactoryType;
		companyId: string;
		saveClicked: boolean;
		setSaveClicked: (value: boolean) => void;
	}>,
) {
	const { t } = useTranslation();

	const contact = useMemo<ContactExtended>(() => {
		return {
			...setContactValues(props.contact),
			contactTypes: [
				...(props.contact?.contactTypes.filter(
					(type) => type !== ContactType.ContactPerson && type !== ContactType.Person,
				) ?? []),
				ContactType.Person,
				ContactType.ContactPerson,
			],
		};
	}, [props.contact]);
	const [createPersonContact] = useCreatePersonContact();
	const [updatePersonContact] = useUpdatePersonContact();

	const [tabIndex, setTabIndex] = useState<number>(0);

	const open = useMemo<boolean>(() => {
		setTabIndex(0);
		return props.open;
	}, [props.open]);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const handleClose = () => {
		props.setOpen(false);
		setErrors({});
		props.setSaveClicked(false);
	};

	const personIdentificationRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);
	const contactInfoRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		localErrors.personIdentification = (await personIdentificationRef.current?.validate()) || {};
		localErrors.contactInfo = (await contactInfoRef.current?.validate()) || {};

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();

		const isValid = await validateFields();

		if (isValid) {
			props.setSaveClicked(true);
			if (contact?.id) {
				updatePersonContact(contact).then(() => {
					props.setContact?.({ ...contact });
					props.refreshData(contact);
					handleClose();
				});
			} else {
				createPersonContact(contact, props.companyId).then((res: Contact) => {
					const newContact = {
						...setContactValues(contact),
						id: res.id,
						contactUsage: contact?.contactUsage ?? [],
						informationId: contact?.informationId ?? "",
					};

					props.setContact?.(newContact);
					props.refreshData(newContact);
					handleClose();
				});
			}
		}
	};

	const tabs = useMemo(
		() => [
			{
				label: t("tab.person-identification"),
				key: TabKeys.PERSON_IDENTIFICATION,
				content: (
					<PersonIdentification
						isDialog={true}
						ref={personIdentificationRef}
						errors={errors.personIdentification as Record<string, object>}
						setErrors={(value: Record<string, boolean | object | string>) =>
							setErrors({
								...errors,
								personIdentification: {
									...(errors.personIdentification as Record<string, object>),
									...value,
								},
							})
						}
						contact={contact}
						setContact={props.setContact}
					/>
				),
			},
			{
				label: t("tab.contact-information"),
				key: TabKeys.CONTACT_INFORMATION,
				content: (
					<ContactInfo
						ref={contactInfoRef}
						errors={errors.contactInfo as Record<string, object>}
						setErrors={(value: Record<string, boolean | object | string>) =>
							setErrors({
								...errors,
								contactInfo: {
									...(errors.contactInfo as Record<string, object>),
									...value,
								},
							})
						}
						contact={contact}
						setContact={props.setContact}
					/>
				),
			},
			{
				label: t("tab.address-information"),
				key: TabKeys.ADDRESS_INFORMATION,
				content: <Address isLoaded={!!contact} contact={contact} setContact={props.setContact} />,
			},
			{
				label: t("tab.department-information"),
				key: TabKeys.DEPARTMENT_INFORMATION,
				content: <DepartmentInfo contact={contact} setContact={props.setContact} />,
			},
			{
				label: t("tab.email-configuration"),
				key: TabKeys.ACTIONS,
				content: <DialogActions contact={contact} setContact={props.setContact} />,
			},
		],

		[t, contact, errors],
	);

	return (
		<DialogTabs
			open={open}
			handleSubmit={handleSubmit}
			handleClose={handleClose}
			setValue={setTabIndex}
			value={tabIndex}
			title={contact?.id ? t("contact.edit") : t("contact.new")}
			tabs={tabs}
			errors={errors}
			saveClicked={props.saveClicked}
			ruleFactory={props.ruleFactory}
			testId="contact-person-dialog"
		/>
	);
}
